/**
 * JewelleryTagViewer — the shared, reusable tag UI used by BOTH:
 *   • JewelleryTagDialog (in-app, authed) and
 *   • JewelleryTagView   (public QR-scan page, no auth).
 *
 * Responsibilities:
 *   - 3 selectable layouts (Classic Card / Hang Tag / Detailed Wide)
 *   - Raw-material view toggle: Expanded (one line per consumed entry, with
 *     notes) OR Collapsed (totalled per classification — carats + pieces)
 *   - A QR code that links to the PUBLIC viewer page ( /t/{id} ) so scanning
 *     opens the full tag on any phone
 *   - Print the full tag at its exact physical size
 *   - Print ONLY the QR code (small label)
 *
 * Tags are built with inline styles so a bare print window reproduces them
 * pixel-for-pixel (1 CSS px = 1/96in, matching the @page inch sizes).
 */

import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import QRCode from 'react-qr-code';
import { Button } from '@/components/ui/button';
import { Printer, QrCode, Rows3, List, Ban } from 'lucide-react';
import { formatGrams, formatCarats } from '@/lib/format';
import { sortByMaterialHierarchy } from '@/lib/materialHierarchy';
import { cleanClassification } from '@/lib/classification';
import { chooseFit, mmToPx, FIT_CANDIDATES } from '@/lib/tagFit';
import { toast } from 'sonner';

// ── Print-safe palette (mirrors ERP tokens; hard-coded so the bare print
//    window / public page need no app CSS). ──────────────────────────────
export const C = {
  ink: '#0B1B2B',
  emerald: '#047857',
  amber: '#92400E',
  violet: '#4B2E83',
  divider: '#E7E3DA',
  muted: '#64748B',
  slate50: '#F8FAFC',
  line: '#E2E8F0',
  white: '#FFFFFF',
};
const SANS = "'Inter', system-ui, -apple-system, 'Segoe UI', sans-serif";
const MONO = "'Inter', ui-sans-serif, system-ui, sans-serif";

const matColor = (t) => (
  t === 'diamond' ? '#0369A1'
    : t === 'color_stone' ? '#BE123C'
      : t === 'pearl' ? '#475569'
        : C.violet
);

export const TAG_LAYOUTS = [
  { id: 'classic', label: 'Classic Card', w: 336, page: '3.5in 2in', qr: 52, desc: 'Business-card size · landscape' },
  { id: 'hangtag', label: 'Hang Tag', w: 216, page: '2.25in 3.5in', qr: 60, desc: 'Classic jewellery tag · portrait' },
  { id: 'detailed', label: 'Detailed', w: 384, page: '4in 3in', qr: 64, desc: 'More room for materials · wide' },
  // Grid layouts size themselves to their content (`fit: true`). `w` here is
  // only a nominal ceiling used for preview scaling; the real printed width is
  // measured from the rendered node so there is no dead gap on wide columns.
  { id: 'grid2', label: 'Grid · 2-Col', w: 470, qr: 54, fit: true, desc: 'Many stones · 2 columns' },
  { id: 'grid3', label: 'Grid · 3-Col', w: 690, qr: 54, fit: true, desc: 'Lots of stones · 3 columns' },
];

// Standalone "Print QR only" sticker sizes, in CSS px (96dpi). Smaller fits
// tiny jewellery tags; larger is easier to scan. Default is Small.
export const QR_LABEL_SIZES = [
  { id: 'xs', label: 'Extra Small', px: 48 },
  { id: 'sm', label: 'Small', px: 64 },
  { id: 'md', label: 'Medium', px: 88 },
  { id: 'lg', label: 'Large', px: 120 },
];

// Tag SIZE presets — a uniform scale factor applied to the WHOLE tag (fonts,
// image, QR, spacing). Large == current design (factor 1.0). Smaller presets
// shrink everything proportionally while keeping every field visible. The
// same factor model powers the custom mm "fit" engine (which computes its own
// factor to hit an exact physical size).
export const TAG_SIZES = [
  { id: 'xs', label: 'Extra Small', factor: 0.62 },
  { id: 'sm', label: 'Small', factor: 0.74 },
  { id: 'md', label: 'Medium', factor: 0.86 },
  { id: 'lg', label: 'Large', factor: 1.0 },
];

export function sizeFactor(sizeId) {
  return (TAG_SIZES.find((z) => z.id === sizeId) || TAG_SIZES[TAG_SIZES.length - 1]).factor;
}

function purityText(name, pct) {
  // Gold purity shows the NAME only (e.g. "14Kt"). The percent is never shown
  // on tags or the public QR page (product requirement).
  const n = (name || '').trim();
  return n || '—';
}

// Normalise a raw material list (already non-gold) to the viewer shape.
function normMaterials(rows) {
  return (rows || []).map((r, i) => {
    const schema = String(r.classification_schema || r.packet_no || '—').trim();
    // Tag Display Name — printed EXACTLY as typed (no cleanup). Falls back to
    // the cleaned classification name when blank.
    const display = String(r.tag_display_name || '').trim();
    return {
      key: `${schema}-${i}`,
      schema: schema.toUpperCase(),          // raw code — used for POLKI matching
      classification: display || cleanClassification(schema),
      carats: Number(r.carats || 0),
      pieces: Number(r.pieces || 0),
      materialType: r.material_type,
      note: (r.issue_note || '').trim(),
    };
  });
}

// ── POLKI weight-fold handling (TAG DISPLAY ONLY) ────────────────────────
// "POLKI-SLV" (silver-set polki) and "POLKI-DAK" are polki weights captured in
// carats. On the TAG we do NOT list them as stone lines — instead we convert
// their carats to grams (1 ct = 0.2 g, the standard gemological conversion)
// and fold that into the product's Net Weight. Matching is done on the raw
// schema code so it still works even when a Tag Display Name is configured.
// This adjustment is applied ONLY here (while building the tag).
const CT_TO_G = 0.2;
const POLKI_WEIGHT_CODES = new Set(['POLKI-SLV', 'POLKI-DAK']);

function splitPolkiSlv(materials) {
  const visible = [];
  let foldCarats = 0;
  for (const m of (materials || [])) {
    const code = String(m.schema || m.classification || '').trim().toUpperCase();
    if (POLKI_WEIGHT_CODES.has(code)) {
      foldCarats += Number(m.carats) || 0;
    } else {
      visible.push(m);
    }
  }
  return { visible, extraNetGrams: foldCarats * CT_TO_G };
}

// Build from the authed detail doc (GET /jewellery/{id}).
export function buildTagDataFromDetail(det) {
  const rm = (det.rm_consumed_snapshot || []).filter((r) => r.material_type !== 'gold');
  const { visible, extraNetGrams } = splitPolkiSlv(sortByMaterialHierarchy(normMaterials(rm)));
  return {
    id: det.id,
    jewelleryNo: det.jewellery_no || '—',
    title: det.title || '',
    jewelleryType: det.jewellery_type || '',
    image: det.image || det.fallback_image || '',
    purityText: purityText(det.gold_purity_name, det.gold_purity_percent),
    gross: Number(det.gross_weight_grams || 0),
    net: Number(det.net_weight_grams || 0) + extraNetGrams,
    materials: visible,
  };
}

// Build from the public endpoint payload (GET /jewellery/public-tag/{id}).
// The public (QR-scan) payload never carries piece counts; we force pieces to
// 0 here as a second lock so a scanned tag can never render "· N pcs" even if
// the backend shape changes. The viewer is always rendered with lockPublic.
export function buildTagDataFromPublic(pub) {
  const noPieces = (pub.materials || []).map((m) => ({ ...m, pieces: 0 }));
  const { visible, extraNetGrams } = splitPolkiSlv(sortByMaterialHierarchy(normMaterials(noPieces)));
  return {
    id: pub.id,
    jewelleryNo: pub.jewellery_no || '—',
    title: pub.title || '',
    jewelleryType: pub.jewellery_type || '',
    image: pub.image || '',
    purityText: purityText(pub.gold_purity_name, pub.gold_purity_percent),
    gross: Number(pub.gross_weight_grams || 0),
    net: Number(pub.net_weight_grams || 0) + extraNetGrams,
    materials: visible,
  };
}

// Collapse: total carats + pieces per classification name (notes dropped —
// collapsed is a per-classification summary).
export function collapseMaterials(materials) {
  const map = new Map();
  for (const m of materials) {
    const key = m.classification;
    if (!map.has(key)) {
      map.set(key, { key, classification: m.classification, materialType: m.materialType, carats: 0, pieces: 0, note: '', lines: 0 });
    }
    const g = map.get(key);
    g.carats += Number(m.carats || 0);
    g.pieces += Number(m.pieces || 0);
    g.lines += 1;
  }
  return Array.from(map.values());
}

// ── Shared tag pieces ────────────────────────────────────────────────────
function ImageBox({ src, size, s = 1 }) {
  const box = {
    width: size * s, height: size * s, flex: '0 0 auto',
    border: `1px solid ${C.line}`, borderRadius: 6 * s, background: C.slate50,
    display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden',
  };
  if (src) {
    return (
      <div style={box}>
        <img src={src} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain', padding: 3 * s }} />
      </div>
    );
  }
  return (
    <div style={{ ...box, flexDirection: 'column' }}>
      <div style={{ fontSize: 9 * s, color: C.muted, fontFamily: SANS }}>No photo</div>
    </div>
  );
}

function WeightPills({ gross, net, fs = 10, s = 1 }) {
  const pill = { flex: 1, border: `1px solid ${C.line}`, borderRadius: 5 * s, padding: `${3 * s}px ${6 * s}px`, background: C.white };
  const cap = { fontSize: (fs - 2) * s, letterSpacing: 0.4, textTransform: 'uppercase', color: C.muted, fontFamily: SANS };
  const val = { fontSize: (fs + 1) * s, fontFamily: MONO, color: C.ink, fontWeight: 600 };
  return (
    <div style={{ display: 'flex', gap: 6 * s }}>
      <div style={pill}><div style={cap}>Gross</div><div style={val}>{formatGrams(gross)} g</div></div>
      <div style={pill}><div style={cap}>Net</div><div style={val}>{formatGrams(net)} g</div></div>
    </div>
  );
}

// Split a list into `cols` down-first chunks (column 1 gets the first
// ceil(n/cols) entries, etc.) so a multi-column tag reads top-to-bottom like
// the old CSS-columns flow, but with content-sized columns.
function chunkColumns(arr, cols) {
  const n = arr.length;
  if (cols <= 1 || n === 0) return [arr];
  const per = Math.ceil(n / cols);
  const out = [];
  for (let c = 0; c < cols; c += 1) {
    const slice = arr.slice(c * per, c * per + per);
    if (slice.length) out.push(slice);
  }
  return out;
}

// Truncate a monospace classification name to fit a pixel box WITHOUT relying
// on CSS overflow:hidden + text-overflow:ellipsis — html2canvas does not honour
// text-overflow and clips the bottom (descenders) of any element it rasterises
// that has overflow:hidden. Monospace glyphs are ~0.6em wide, so a char budget
// derived from the box width is accurate and scale-invariant (box and font both
// scale by the same factor). Keeping the text node overflow:visible means no
// bottom clipping in the generated PDF/print image.
function truncMono(str, fs, maxWidthPx = 150) {
  const s = String(str == null ? '' : str);
  const maxChars = Math.max(6, Math.floor(maxWidthPx / (fs * 0.6)));
  if (s.length <= maxChars) return s;
  return `${s.slice(0, Math.max(1, maxChars - 1))}\u2026`;
}

function MaterialRow({ m, fs, showNotes, showPieces, s = 1 }) {
  return (
    <div style={{ breakInside: 'avoid', WebkitColumnBreakInside: 'avoid', marginBottom: 3 * s }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 * s }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 4 * s, minWidth: 0 }}>
          <span style={{ width: 6 * s, height: 6 * s, borderRadius: 999, background: matColor(m.materialType), flex: '0 0 auto' }} />
          <span style={{ fontFamily: MONO, fontWeight: 600, fontSize: fs * s, lineHeight: 1.45, color: C.ink, whiteSpace: 'nowrap', overflow: 'visible', flex: '0 0 auto' }} title={m.classification}>
            {truncMono(m.classification, fs, 140)}
          </span>
        </span>
        <span style={{ fontFamily: MONO, fontSize: fs * s, lineHeight: 1.45, color: C.ink, whiteSpace: 'nowrap', flex: '0 0 auto' }}>
          {formatCarats(m.carats)} ct{showPieces ? ` \u00b7 ${m.pieces} pcs` : ''}
        </span>
      </div>
      {showNotes && m.note ? (
        <div style={{ marginLeft: 10 * s, fontSize: (fs - 1.5) * s, color: C.muted, fontStyle: 'italic', fontFamily: SANS, lineHeight: 1.3 }}>
          {m.note}
        </div>
      ) : null}
    </div>
  );
}

function MaterialRows({ materials, fs = 10, showNotes = true, showPieces = true, columns = 1, s = 1 }) {
  if (!materials.length) {
    return <div style={{ fontSize: (fs - 1) * s, color: C.muted, fontStyle: 'italic', fontFamily: SANS }}>No stones in this piece.</div>;
  }
  const multi = columns > 1;
  if (multi) {
    // Content-sized columns joined by a thin divider. Each column is only as
    // wide as its widest row (capped), so the classification name and the
    // ct/pcs value stay close instead of being pushed to opposite edges of a
    // fixed-width tag.
    const chunks = chunkColumns(materials, columns);
    return (
      <div style={{ display: 'flex', alignItems: 'flex-start' }}>
        {chunks.map((col, ci) => (
          <div
            key={ci}
            style={{
              display: 'flex', flexDirection: 'column', flex: '0 0 auto',
              width: 'max-content', maxWidth: 260 * s,
              paddingLeft: ci > 0 ? 12 * s : 0, marginLeft: ci > 0 ? 12 * s : 0,
              borderLeft: ci > 0 ? `1px solid ${C.divider}` : 'none',
            }}
          >
            {col.map((m) => (
              <MaterialRow key={m.key} m={m} fs={fs} showNotes={showNotes} showPieces={showPieces} s={s} />
            ))}
          </div>
        ))}
      </div>
    );
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 3 * s }}>
      {materials.map((m) => (
        <MaterialRow key={m.key} m={m} fs={fs} showNotes={showNotes} showPieces={showPieces} s={s} />
      ))}
    </div>
  );
}

function SectionLabel({ children, fs = 8, s = 1 }) {
  return (
    <div style={{ fontSize: fs * s, letterSpacing: 0.6, textTransform: 'uppercase', color: C.muted, fontFamily: SANS, fontWeight: 600 }}>
      {children}
    </div>
  );
}

function QrChip({ value, size, s = 1 }) {
  return (
    <div style={{ background: C.white, padding: 3 * s, border: `1px solid ${C.line}`, borderRadius: 5 * s, flex: '0 0 auto' }}>
      <QRCode value={value || ' '} size={size} style={{ height: size, width: size }} />
    </div>
  );
}

// ── Layouts ───────────────────────────────────────────────────────────────
function LayoutClassic({ data, materials, showNotes, qr, qrValue, showQr, showPieces, s = 1, purityPos = 'header' }) {
  const purity = (
    <div style={{ fontFamily: SANS, fontSize: 10 * s, color: C.amber, fontWeight: 600, textAlign: purityPos === 'belowImage' ? 'center' : 'left' }}>{data.purityText}</div>
  );
  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: 192 * s }}>
      <div style={{ height: 5 * s, background: C.violet }} />
      <div style={{ display: 'flex', gap: 10 * s, padding: 10 * s, flex: 1 }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 * s, flex: '0 0 auto' }}>
          <ImageBox src={data.image} size={showQr ? 82 : 104} s={s} />
          {showQr ? <QrChip value={qrValue} size={qr} s={s} /> : null}
          {purityPos === 'belowImage' ? purity : null}
        </div>
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 5 * s }}>
          <div style={{ fontFamily: MONO, fontSize: 15 * s, fontWeight: 700, color: C.emerald, lineHeight: 1 }}>{data.jewelleryNo}</div>
          {purityPos === 'header' ? purity : null}
          <WeightPills gross={data.gross} net={data.net} fs={9} s={s} />
          <div style={{ borderTop: `1px solid ${C.divider}`, paddingTop: 4 * s, display: 'flex', flexDirection: 'column', gap: 3 * s, minWidth: 0 }}>
            <SectionLabel s={s}>Raw material</SectionLabel>
            <MaterialRows materials={materials} fs={9.5} showNotes={showNotes} showPieces={showPieces} s={s} />
          </div>
        </div>
      </div>
    </div>
  );
}

function LayoutHangTag({ data, materials, showNotes, qr, qrValue, showQr, showPieces, s = 1 }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: 336 * s }}>
      <div style={{ height: 5 * s, background: C.violet }} />
      <div style={{ padding: 10 * s, display: 'flex', flexDirection: 'column', gap: 7 * s, flex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6 * s }}>
          <div style={{ fontFamily: MONO, fontSize: 14 * s, fontWeight: 700, color: C.emerald, lineHeight: 1 }}>{data.jewelleryNo}</div>
          {showQr ? <QrChip value={qrValue} size={qr} s={s} /> : null}
        </div>
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <ImageBox src={data.image} size={118} s={s} />
        </div>
        <div style={{ fontFamily: SANS, fontSize: 11 * s, color: C.amber, fontWeight: 600, textAlign: 'center' }}>{data.purityText}</div>
        <WeightPills gross={data.gross} net={data.net} fs={10} s={s} />
        <div style={{ borderTop: `1px solid ${C.divider}`, paddingTop: 5 * s, display: 'flex', flexDirection: 'column', gap: 4 * s, minWidth: 0 }}>
          <SectionLabel s={s}>Raw material</SectionLabel>
          <MaterialRows materials={materials} fs={10} showNotes={showNotes} showPieces={showPieces} s={s} />
        </div>
      </div>
    </div>
  );
}

function LayoutDetailed({ data, materials, showNotes, qr, qrValue, showQr, showPieces, s = 1, purityPos = 'header' }) {
  const purity = (
    <div style={{ fontFamily: SANS, fontSize: 11 * s, color: C.amber, fontWeight: 600, textAlign: purityPos === 'belowImage' ? 'center' : 'left' }}>{data.purityText}</div>
  );
  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: 288 * s }}>
      <div style={{ height: 6 * s, background: C.violet }} />
      <div style={{ display: 'flex', gap: 12 * s, padding: 12 * s, flex: 1 }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 * s, flex: '0 0 auto', width: 118 * s }}>
          <ImageBox src={data.image} size={118} s={s} />
          {showQr ? <QrChip value={qrValue} size={qr} s={s} /> : null}
          {showQr ? <div style={{ fontFamily: SANS, fontSize: 8 * s, color: C.muted, textAlign: 'center', lineHeight: 1.2 }}>Scan for live tag</div> : null}
          {purityPos === 'belowImage' ? purity : null}
        </div>
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 7 * s }}>
          <div>
            <div style={{ fontFamily: MONO, fontSize: 18 * s, fontWeight: 700, color: C.emerald, lineHeight: 1 }}>{data.jewelleryNo}</div>
            {data.title ? <div style={{ fontFamily: SANS, fontSize: 10 * s, color: C.ink, marginTop: 3 * s, lineHeight: 1.25 }}>{data.title}</div> : null}
          </div>
          {purityPos === 'header' ? purity : null}
          <WeightPills gross={data.gross} net={data.net} fs={10} s={s} />
          <div style={{ borderTop: `1px solid ${C.divider}`, paddingTop: 6 * s, display: 'flex', flexDirection: 'column', gap: 4 * s, minWidth: 0 }}>
            <SectionLabel s={s}>Raw material used</SectionLabel>
            <MaterialRows materials={materials} fs={10.5} showNotes={showNotes} showPieces={showPieces} s={s} />
          </div>
        </div>
      </div>
    </div>
  );
}

// Column-flow layout for pieces with MANY raw-material entries: a top header
// band (image + identity + weights + optional QR) with the raw-material list
// flowing into `cols` columns below, so the tag stays short vertically and the
// whole tag is visible on one screen instead of a long scroll.
function LayoutGrid({ data, materials, showNotes, qr, qrValue, showQr, showPieces, cols = 2, s = 1 }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      <div style={{ height: 5 * s, background: C.violet }} />
      <div style={{ padding: 12 * s, display: 'flex', flexDirection: 'column', gap: 9 * s }}>
        <div style={{ display: 'flex', gap: 12 * s, alignItems: 'flex-start' }}>
          <ImageBox src={data.image} size={84} s={s} />
          <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 4 * s }}>
            <div style={{ fontFamily: MONO, fontSize: 16 * s, fontWeight: 700, color: C.emerald, lineHeight: 1 }}>{data.jewelleryNo}</div>
            <div style={{ fontFamily: SANS, fontSize: 10 * s, color: C.amber, fontWeight: 600 }}>{data.purityText}</div>
            <WeightPills gross={data.gross} net={data.net} fs={9} s={s} />
          </div>
          {showQr ? <QrChip value={qrValue} size={qr} s={s} /> : null}
        </div>
        <div style={{ borderTop: `1px solid ${C.divider}`, paddingTop: 7 * s, display: 'flex', flexDirection: 'column', gap: 5 * s, minWidth: 0 }}>
          <SectionLabel s={s}>Raw material</SectionLabel>
          <MaterialRows materials={materials} fs={9.5} showNotes={showNotes} showPieces={showPieces} columns={cols} s={s} />
        </div>
      </div>
    </div>
  );
}

function renderLayout(layoutId, props) {
  if (layoutId === 'hangtag') return <LayoutHangTag {...props} />;
  if (layoutId === 'detailed') return <LayoutDetailed {...props} />;
  if (layoutId === 'grid2') return <LayoutGrid {...props} cols={2} />;
  if (layoutId === 'grid3') return <LayoutGrid {...props} cols={3} />;
  return <LayoutClassic {...props} />;
}

export const FONT_LINKS = `
<link rel="preconnect" href="https://fonts.googleapis.com">
<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
<link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Gloock&display=swap" rel="stylesheet">`;

// Generic print-window opener. `pageSize` accepts any CSS @page size token
// (e.g. '3.5in 2in', 'A4'); `margin` is the @page margin. Shared by the
// single-tag prints and the batch sheet so the print styling stays identical.
export function openPrintWindow(html, { pageSize = 'auto', title = 'Print', margin = '0' } = {}) {
  const win = window.open('', '_blank', 'width=820,height=720');
  if (!win) {
    toast.error('Please allow pop-ups to print.');
    return;
  }
  win.document.write(`<!doctype html><html><head><title>${title}</title>
<meta charset="utf-8" />${FONT_LINKS}
<style>
  @page { size: ${pageSize}; margin: ${margin}; }
  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; background: #fff; }
  body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
</style></head><body>${html}</body></html>`);
  win.document.close();
  win.focus();
  setTimeout(() => { try { win.print(); } catch (_) { /* noop */ } }, 500);
}

function printNodeHtml(html, pageSize, title) {
  openPrintWindow(html, { pageSize, title, margin: '0' });
}

// Build the public QR target URL for a tag. Priority:
//   1) explicit `origin` override,
//   2) REACT_APP_PUBLIC_TAG_BASE_URL — the neutral white-label domain the
//      printed tags should always point at (e.g. https://rosentiquestag.com),
//   3) the current window origin (preview / fallback).
export function buildQrValue(data, origin) {
  const envBase = (process.env.REACT_APP_PUBLIC_TAG_BASE_URL || '').replace(/\/+$/, '');
  const base = origin || envBase || (typeof window !== 'undefined' ? window.location.origin : '');
  return data?.id ? `${base}/t/${data.id}` : (data?.jewelleryNo || ' ');
}

// Pick a sensible DEFAULT layout from how many raw-material rows will show:
//   <= 5  -> Classic Card (compact)
//   6-10  -> Grid 2-Col
//   > 10  -> Grid 3-Col
// The user can still switch freely afterwards.
export function computeDefaultLayout(data, rmMode) {
  if (!data) return 'classic';
  const list = rmMode === 'collapsed' ? collapseMaterials(data.materials || []) : (data.materials || []);
  const n = list.length;
  if (n > 10) return 'grid3';
  if (n > 5) return 'grid2';
  return 'classic';
}

export function JewelleryTagViewer({ data, origin, defaultShowQr = true, piecesToggle = false, rmToggle = true, defaultRmMode = 'expanded', defaultSizeId = 'lg', sizeToggle = true, lockPublic = false }) {
  // Public QR-scan view is LOCKED: a single Classic tag only — no layout
  // switcher, no raw-material/pieces/QR toggles, no print actions. It is
  // always collapsed and never shows pieces, and scrolls vertically on a
  // phone when a piece has many raw-material lines.
  const effectiveRmMode = lockPublic ? 'collapsed' : defaultRmMode;
  const [layoutId, setLayoutId] = useState(() => (lockPublic ? 'classic' : computeDefaultLayout(data, defaultRmMode)));
  const [rmMode, setRmMode] = useState(effectiveRmMode); // 'expanded' | 'collapsed'
  const [showQr, setShowQr] = useState(lockPublic ? false : defaultShowQr);
  const [qrSizeId, setQrSizeId] = useState('sm'); // standalone "Print QR only" size
  const [sizeId, setSizeId] = useState(lockPublic ? 'lg' : defaultSizeId); // tag SIZE preset
  const [customOn, setCustomOn] = useState(false); // custom mm size mode
  const [customW, setCustomW] = useState(40); // label width in mm
  const [customH, setCustomH] = useState(25); // label height in mm
  // Pieces (pcs) visibility. Portal dialog shows a toggle (default ON); the
  // public QR-scan viewer never shows pieces (locked OFF).
  const [showPieces, setShowPieces] = useState(lockPublic ? false : piecesToggle);
  const [scale, setScale] = useState(1.3);
  const [measured, setMeasured] = useState([]); // natural sizes of fit candidates
  const measureRefs = useRef([]);
  const tagRef = useRef(null);
  const qrOnlyRef = useRef(null);
  const qrImgRef = useRef(null);
  const previewRef = useRef(null);

  const layoutCfg = useMemo(() => TAG_LAYOUTS.find((l) => l.id === layoutId) || TAG_LAYOUTS[0], [layoutId]);
  const sizeS = useMemo(() => sizeFactor(sizeId), [sizeId]);
  const qrPx = useMemo(() => (QR_LABEL_SIZES.find((q) => q.id === qrSizeId) || QR_LABEL_SIZES[0]).px, [qrSizeId]);
  const qrValue = useMemo(() => buildQrValue(data, origin), [origin, data]);

  const materials = useMemo(() => {
    if (!data) return [];
    return rmMode === 'collapsed' ? collapseMaterials(data.materials) : data.materials;
  }, [data, rmMode]);
  const showNotes = rmMode === 'expanded';

  // Measure the natural (s=1) size of each fit candidate so the custom-mm fit
  // engine can pick the layout + scale that fills an exact label size. Runs
  // whenever the content that affects size changes.
  useLayoutEffect(() => {
    if (lockPublic || !data) return;
    const arr = FIT_CANDIDATES.map((c, i) => {
      const node = measureRefs.current[i];
      if (!node) return { ...c, w: 0, h: 0 };
      const r = node.getBoundingClientRect();
      return { ...c, w: r.width, h: r.height };
    });
    setMeasured(arr);
  }, [data, materials, showQr, showPieces, rmMode, lockPublic]);

  // Custom-mm fit result (null unless custom mode is active with valid dims).
  const customWpx = mmToPx(customW);
  const customHpx = mmToPx(customH);
  const fit = (!lockPublic && customOn && measured.length && customWpx > 10 && customHpx > 10)
    ? chooseFit(measured, customWpx, customHpx)
    : null;
  // Effective render settings: custom-fit overrides the preset size/layout.
  const effLayoutId = fit ? fit.layoutId : layoutId;
  const effPurityPos = fit ? fit.purityPos : 'header';
  const effScale = fit ? fit.scale : sizeS;
  const effCfg = useMemo(() => TAG_LAYOUTS.find((l) => l.id === effLayoutId) || TAG_LAYOUTS[0], [effLayoutId]);
  const effMaterials = materials; // same content; layout differs

  // Fit the preview tag to the available width (responsive on phones).
  useEffect(() => {
    const el = previewRef.current;
    if (!el) return undefined;
    const compute = () => {
      const avail = el.clientWidth - 28;
      // When custom mode is on we fit to the exact label box; otherwise to the
      // natural (scaled) width of the tag at the current size preset.
      const natW = customOn && customWpx > 10 ? customWpx : effCfg.w * effScale;
      // Wide / grid ("fit") layouts must never be enlarged — enlarging them
      // overflows the dialog and triggers a zoom/resize growth loop. Narrow
      // layouts may still be enlarged for readability.
      const maxScale = (natW >= 500 || (effCfg.fit && !customOn)) ? 1 : 1.55;
      const sc = Math.min(maxScale, Math.max(0.4, avail / natW));
      setScale(Number.isFinite(sc) && sc > 0 ? sc : 1);
    };
    compute();
    let ro;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(compute);
      ro.observe(el);
    }
    return () => { if (ro) ro.disconnect(); };
  }, [effCfg.w, effCfg.fit, effScale, customOn, customWpx]);

  const handlePrint = () => {
    const node = tagRef.current;
    if (!node || !data) return;
    // Custom mm label: print on an EXACT physical page. Block if the data does
    // not fit at a readable scale (user is warned inline on the preview too).
    if (customOn) {
      if (fit && !fit.fits) {
        toast.error(`Tag does not fit in ${customW}×${customH} mm — increase the size. Some data would be skipped.`);
        return;
      }
      printNodeHtml(node.outerHTML, `${customW}mm ${customH}mm`, `Tag · ${data.jewelleryNo}`);
      return;
    }
    // Grid layouts are content-sized (no fixed physical page). Measure the
    // rendered node (dividing out the preview zoom) to produce a tight page
    // that matches the dynamic tag width. Fixed-size layouts keep their exact
    // physical page sizes, SCALED by the chosen size preset.
    let pageSize = layoutCfg.page;
    if (layoutCfg.fit || !pageSize) {
      const rect = node.getBoundingClientRect();
      const sc = scale > 0 ? scale : 1;
      const wIn = ((rect.width / sc) + 2) / 96;
      const hIn = ((rect.height / sc) + 2) / 96;
      pageSize = `${wIn.toFixed(2)}in ${hIn.toFixed(2)}in`;
    } else if (sizeS !== 1) {
      // Scale the fixed inch page size (e.g. "3.5in 2in") by the size preset.
      pageSize = pageSize.replace(/([\d.]+)in/g, (_, n) => `${(Number(n) * sizeS).toFixed(2)}in`);
    }
    printNodeHtml(node.outerHTML, pageSize, `Tag · ${data.jewelleryNo}`);
  };
  const handlePrintQrOnly = () => {
    const node = qrOnlyRef.current;
    if (!node || !data) return;
    // Tight page around the QR + jewellery-no line, so the printed sticker is
    // only as big as the chosen QR size (fits small jewellery tags).
    const wIn = (qrPx + 28) / 96;
    const hIn = (qrPx + 28 + 26) / 96;
    printNodeHtml(node.outerHTML, `${wIn.toFixed(2)}in ${hIn.toFixed(2)}in`, `QR · ${data.jewelleryNo}`);
  };

  const handlePrintQrImage = () => {
    const node = qrImgRef.current;
    if (!node || !data) return;
    // QR + photo side by side, both at the chosen QR size. Tight page.
    const wIn = (2 * qrPx + 42) / 96;
    const hIn = (qrPx + 24) / 96;
    printNodeHtml(node.outerHTML, `${wIn.toFixed(2)}in ${hIn.toFixed(2)}in`, `QR+Img · ${data.jewelleryNo}`);
  };

  if (!data) return null;

  return (
    <div className="space-y-3" data-testid="jewellery-tag-viewer">
      {!lockPublic && (
      <>
      {/* Layout switcher */}
      <div className="flex flex-wrap gap-2" data-testid="jewellery-tag-layout-tabs">
        {TAG_LAYOUTS.map((l) => {
          const active = l.id === layoutId;
          return (
            <button
              key={l.id}
              type="button"
              onClick={() => setLayoutId(l.id)}
              aria-pressed={active}
              data-testid={`jewellery-tag-layout-${l.id}`}
              className={[
                'flex-1 min-w-[108px] rounded-md border px-3 py-2 text-left transition-colors',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--jewellery-icon)]',
                active ? 'border-[var(--jewellery-icon)] bg-[var(--jewellery-bg)]' : 'border-slate-200 bg-white hover:border-[var(--jewellery-border)] hover:bg-slate-50',
              ].join(' ')}
            >
              <div className={`text-[12px] font-medium ${active ? 'text-[var(--jewellery-icon)]' : 'text-[var(--ink-navy)]'}`}>{l.label}</div>
              <div className="text-[10px] text-slate-500 mt-0.5 leading-tight">{l.desc}</div>
            </button>
          );
        })}
      </div>

      {/* Controls row: raw-material view + QR on/off */}
      <div className="flex flex-wrap items-center gap-x-5 gap-y-2" data-testid="jewellery-tag-rm-mode">
        {rmToggle ? (
          <div className="flex items-center gap-2">
            <span className="text-[11px] text-slate-500">Raw material:</span>
            <div className="inline-flex rounded-md border border-slate-200 overflow-hidden">
              <button
                type="button"
                onClick={() => setRmMode('expanded')}
                aria-pressed={rmMode === 'expanded'}
                data-testid="jewellery-tag-rm-expanded"
                className={`inline-flex items-center gap-1 px-2.5 py-1 text-[11px] transition-colors ${rmMode === 'expanded' ? 'bg-[var(--jewellery-bg)] text-[var(--jewellery-icon)]' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
              >
                <List size={12} /> Expanded
              </button>
              <button
                type="button"
                onClick={() => setRmMode('collapsed')}
                aria-pressed={rmMode === 'collapsed'}
                data-testid="jewellery-tag-rm-collapsed"
                className={`inline-flex items-center gap-1 px-2.5 py-1 text-[11px] border-l border-slate-200 transition-colors ${rmMode === 'collapsed' ? 'bg-[var(--jewellery-bg)] text-[var(--jewellery-icon)]' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
              >
                <Rows3 size={12} /> Collapsed
              </button>
            </div>
          </div>
        ) : null}

        <div className="flex items-center gap-2">
          <span className="text-[11px] text-slate-500">QR code:</span>
          <div className="inline-flex rounded-md border border-slate-200 overflow-hidden">
            <button
              type="button"
              onClick={() => setShowQr(true)}
              aria-pressed={showQr}
              data-testid="jewellery-tag-qr-on"
              className={`inline-flex items-center gap-1 px-2.5 py-1 text-[11px] transition-colors ${showQr ? 'bg-[var(--jewellery-bg)] text-[var(--jewellery-icon)]' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
            >
              <QrCode size={12} /> With QR
            </button>
            <button
              type="button"
              onClick={() => setShowQr(false)}
              aria-pressed={!showQr}
              data-testid="jewellery-tag-qr-off"
              className={`inline-flex items-center gap-1 px-2.5 py-1 text-[11px] border-l border-slate-200 transition-colors ${!showQr ? 'bg-[var(--jewellery-bg)] text-[var(--jewellery-icon)]' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
            >
              <Ban size={12} /> No QR
            </button>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <span className="text-[11px] text-slate-500">QR print size:</span>
          <div className="inline-flex rounded-md border border-slate-200 overflow-hidden" data-testid="jewellery-tag-qr-size">
            {QR_LABEL_SIZES.map((q, i) => (
              <button
                key={q.id}
                type="button"
                onClick={() => setQrSizeId(q.id)}
                aria-pressed={qrSizeId === q.id}
                data-testid={`jewellery-tag-qr-size-${q.id}`}
                className={`px-2.5 py-1 text-[11px] transition-colors ${i > 0 ? 'border-l border-slate-200' : ''} ${qrSizeId === q.id ? 'bg-[var(--jewellery-bg)] text-[var(--jewellery-icon)]' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
              >
                {q.label}
              </button>
            ))}
          </div>
        </div>

        {piecesToggle ? (
          <div className="flex items-center gap-2">
            <span className="text-[11px] text-slate-500">Pieces:</span>
            <div className="inline-flex rounded-md border border-slate-200 overflow-hidden" data-testid="jewellery-tag-pieces">
              <button
                type="button"
                onClick={() => setShowPieces(true)}
                aria-pressed={showPieces}
                data-testid="jewellery-tag-pieces-on"
                className={`inline-flex items-center gap-1 px-2.5 py-1 text-[11px] transition-colors ${showPieces ? 'bg-[var(--jewellery-bg)] text-[var(--jewellery-icon)]' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
              >
                With pieces
              </button>
              <button
                type="button"
                onClick={() => setShowPieces(false)}
                aria-pressed={!showPieces}
                data-testid="jewellery-tag-pieces-off"
                className={`inline-flex items-center gap-1 px-2.5 py-1 text-[11px] border-l border-slate-200 transition-colors ${!showPieces ? 'bg-[var(--jewellery-bg)] text-[var(--jewellery-icon)]' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
              >
                Without pieces
              </button>
            </div>
          </div>
        ) : null}

        {sizeToggle ? (
          <div className="flex items-center gap-2">
            <span className="text-[11px] text-slate-500">Tag size:</span>
            <div className="inline-flex rounded-md border border-slate-200 overflow-hidden" data-testid="jewellery-tag-size">
              {TAG_SIZES.map((z, i) => (
                <button
                  key={z.id}
                  type="button"
                  onClick={() => { setSizeId(z.id); setCustomOn(false); }}
                  aria-pressed={!customOn && sizeId === z.id}
                  data-testid={`jewellery-tag-size-${z.id}`}
                  className={`px-2.5 py-1 text-[11px] transition-colors ${i > 0 ? 'border-l border-slate-200' : ''} ${!customOn && sizeId === z.id ? 'bg-[var(--jewellery-bg)] text-[var(--jewellery-icon)]' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
                >
                  {z.label}
                </button>
              ))}
              <button
                type="button"
                onClick={() => setCustomOn(true)}
                aria-pressed={customOn}
                data-testid="jewellery-tag-size-custom"
                className={`px-2.5 py-1 text-[11px] border-l border-slate-200 transition-colors ${customOn ? 'bg-[var(--jewellery-bg)] text-[var(--jewellery-icon)]' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
              >
                Custom (mm)
              </button>
            </div>
            {customOn ? (
              <div className="flex items-center gap-1.5" data-testid="jewellery-tag-custom-mm">
                <input
                  type="number" min="5" max="300" value={customW}
                  onChange={(e) => setCustomW(Math.max(0, Number(e.target.value) || 0))}
                  data-testid="jewellery-tag-custom-w"
                  className="w-14 h-7 rounded-md border border-slate-200 px-2 text-[11px] font-mono focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--jewellery-icon)]"
                  aria-label="Label width in mm"
                />
                <span className="text-[11px] text-slate-400">×</span>
                <input
                  type="number" min="5" max="300" value={customH}
                  onChange={(e) => setCustomH(Math.max(0, Number(e.target.value) || 0))}
                  data-testid="jewellery-tag-custom-h"
                  className="w-14 h-7 rounded-md border border-slate-200 px-2 text-[11px] font-mono focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--jewellery-icon)]"
                  aria-label="Label height in mm"
                />
                <span className="text-[11px] text-slate-400">mm</span>
                {fit && fit.fits ? (
                  <span className="text-[10px] text-emerald-700" data-testid="jewellery-tag-fit-ok">Fits ✓ ({effLayoutId})</span>
                ) : null}
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
      </>
      )}

      {/* Inline non-fit warning for custom label sizes */}
      {!lockPublic && customOn && fit && !fit.fits ? (
        <div
          data-testid="jewellery-tag-fit-warning"
          className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-[12px] text-amber-800 flex items-start gap-2"
        >
          <Ban size={14} className="mt-0.5 flex-shrink-0" />
          <span>
            This tag does not fit in <strong>{customW}×{customH} mm</strong>. The content below is clipped — some data would be skipped on the printed label. Increase the size or reduce the content. Printing is disabled until it fits.
          </span>
        </div>
      ) : null}

      {/* Preview */}
      <div
        ref={previewRef}
        className={lockPublic
          ? 'flex items-start justify-center overflow-auto py-2'
          : 'rounded-lg border border-dashed border-slate-300 bg-slate-100 p-4 flex items-start justify-center min-h-[260px] overflow-auto'}
        data-testid="jewellery-tag-preview"
      >
        <div style={{ zoom: scale }}>
          {customOn ? (
            <div
              style={{ width: customWpx, height: customHpx, border: `1px dashed ${fit && !fit.fits ? '#b45309' : C.line}`, background: C.white, boxSizing: 'border-box', overflow: 'hidden', boxShadow: '0 1px 3px rgba(15,23,42,0.12)' }}
            >
              <div
                ref={tagRef}
                data-testid="jewellery-tag-render"
                style={{ width: effCfg.fit ? 'fit-content' : effCfg.w * effScale, maxWidth: effCfg.w * effScale, background: C.white, border: `1px solid ${C.ink}`, borderRadius: 8 * effScale, overflow: 'hidden', fontFamily: SANS, color: C.ink, fontVariantNumeric: 'tabular-nums slashed-zero' }}
              >
                {renderLayout(effLayoutId, { data, materials: effMaterials, showNotes, qr: effCfg.qr * effScale, qrValue, showQr, showPieces, s: effScale, purityPos: effPurityPos })}
              </div>
            </div>
          ) : (
            <div
              ref={tagRef}
              data-testid="jewellery-tag-render"
              style={{ width: effCfg.fit ? 'fit-content' : effCfg.w * effScale, maxWidth: effCfg.w * effScale, background: C.white, border: `1px solid ${C.ink}`, borderRadius: 8 * effScale, overflow: 'hidden', fontFamily: SANS, color: C.ink, fontVariantNumeric: 'tabular-nums slashed-zero', boxShadow: '0 1px 3px rgba(15,23,42,0.12)' }}
            >
              {renderLayout(effLayoutId, { data, materials: effMaterials, showNotes, qr: effCfg.qr * effScale, qrValue, showQr, showPieces, s: effScale, purityPos: effPurityPos })}
            </div>
          )}
        </div>
      </div>

      {/* Actions */}
      {!lockPublic && (
      <div className="flex flex-wrap items-center justify-end gap-2">
        <Button
          variant="outline"
          onClick={handlePrintQrOnly}
          data-testid="jewellery-tag-print-qr-button"
          className="border-[var(--jewellery-border)] text-[var(--jewellery-icon)] hover:bg-[var(--jewellery-bg)]"
        >
          <QrCode size={15} className="mr-1.5" /> Print QR only
        </Button>
        <Button
          variant="outline"
          onClick={handlePrintQrImage}
          data-testid="jewellery-tag-print-qr-image-button"
          className="border-[var(--jewellery-border)] text-[var(--jewellery-icon)] hover:bg-[var(--jewellery-bg)]"
        >
          <QrCode size={15} className="mr-1.5" /> Print QR + Image
        </Button>
        <Button
          onClick={handlePrint}
          disabled={customOn && fit && !fit.fits}
          data-testid="jewellery-tag-print-button"
          className="bg-[var(--ink-navy)] hover:bg-[var(--ink-navy-2)] text-white disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Printer size={15} className="mr-1.5" /> Print tag
        </Button>
      </div>
      )}

      {/* Off-screen measurement nodes — render each fit candidate at natural
          (s=1) size so the custom-mm fit engine can measure and choose the
          best layout + scale. Not shown to the user. */}
      {!lockPublic ? (
        <div style={{ position: 'absolute', left: -99999, top: 0 }} aria-hidden="true" data-testid="jewellery-tag-measure">
          {FIT_CANDIDATES.map((c, i) => {
            const mcfg = TAG_LAYOUTS.find((l) => l.id === c.layoutId) || TAG_LAYOUTS[0];
            return (
              <div
                key={`${c.layoutId}-${c.purityPos}`}
                ref={(el) => { measureRefs.current[i] = el; }}
                style={{ display: 'inline-block', verticalAlign: 'top', width: mcfg.fit ? 'fit-content' : mcfg.w, maxWidth: mcfg.w, border: `1px solid ${C.ink}`, background: C.white, overflow: 'hidden', fontFamily: SANS, fontVariantNumeric: 'tabular-nums slashed-zero' }}
              >
                {renderLayout(c.layoutId, { data, materials, showNotes, qr: mcfg.qr, qrValue, showQr, showPieces, s: 1, purityPos: c.purityPos })}
              </div>
            );
          })}
        </div>
      ) : null}

      {/* Off-screen QR-only node (for Print QR only) */}
      <div style={{ position: 'absolute', left: -99999, top: 0 }} aria-hidden="true">
        <div
          ref={qrOnlyRef}
          style={{ width: qrPx + 28, padding: 10, background: C.white, textAlign: 'center', fontFamily: SANS, fontVariantNumeric: 'tabular-nums slashed-zero' }}
        >
          <div style={{ display: 'inline-block', padding: 4, border: `1px solid ${C.line}`, borderRadius: 6 }}>
            <QRCode value={qrValue} size={qrPx} style={{ height: qrPx, width: qrPx }} />
          </div>
          <div style={{ marginTop: 6, fontFamily: MONO, fontSize: 12, fontWeight: 700, color: C.emerald }}>{data.jewelleryNo}</div>
        </div>
      </div>

      {/* Off-screen QR + Image node (for Print QR + Image) — QR and photo side
          by side, the photo sized to match the chosen QR size. */}
      <div style={{ position: 'absolute', left: -99999, top: 0 }} aria-hidden="true">
        <div
          ref={qrImgRef}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: 10, background: C.white, fontVariantNumeric: 'tabular-nums slashed-zero' }}
        >
          <div style={{ display: 'inline-flex', padding: 4, border: `1px solid ${C.line}`, borderRadius: 6 }}>
            <QRCode value={qrValue} size={qrPx} style={{ height: qrPx, width: qrPx }} />
          </div>
          <ImageBox src={data.image} size={qrPx} />
        </div>
      </div>
    </div>
  );
}

export default JewelleryTagViewer;

// ── Batch sheet item ───────────────────────────────────────────────────────
// One print-ready item for the batch sheet. `mode` picks the output:
//   • 'full'  — the complete tag card (auto-sized), QR included
//   • 'qr'    — a QR-only sticker with the jewellery number
//   • 'qrimg' — QR + photo side by side with the jewellery number
// Items render as inline-blocks with `break-inside: avoid` so they flow onto
// an A4 sheet, multiple per page, without ever splitting across a page break.
export function BatchTagItem({
  data,
  mode = 'full',
  layoutId = 'classic',
  rmMode = 'expanded',
  showPieces = false,
  showQr = true,
  qrPx = 64,
  sizeId = 'lg',
  purityPos = 'header',
  scaleOverride = null,
  boxW = null,
  boxH = null,
  boxWarn = false,
  origin,
}) {
  if (!data) return null;
  const qrValue = buildQrValue(data, origin);
  const s = scaleOverride != null ? scaleOverride : sizeFactor(sizeId);
  const wrap = {
    display: 'inline-block', verticalAlign: 'top',
    breakInside: 'avoid', WebkitColumnBreakInside: 'avoid', pageBreakInside: 'avoid',
    margin: 6,
  };

  if (mode === 'qr') {
    return (
      <div style={wrap}>
        <div style={{ width: qrPx + 28, padding: 10, background: C.white, textAlign: 'center', fontFamily: SANS, border: `1px solid ${C.line}`, borderRadius: 8, fontVariantNumeric: 'tabular-nums slashed-zero' }}>
          <div style={{ display: 'inline-block', padding: 4, border: `1px solid ${C.line}`, borderRadius: 6 }}>
            <QRCode value={qrValue} size={qrPx} style={{ height: qrPx, width: qrPx }} />
          </div>
          <div style={{ marginTop: 6, fontFamily: MONO, fontSize: 12, fontWeight: 700, color: C.emerald }}>{data.jewelleryNo}</div>
        </div>
      </div>
    );
  }

  if (mode === 'qrimg') {
    return (
      <div style={wrap}>
        <div style={{ background: C.white, border: `1px solid ${C.line}`, borderRadius: 8, padding: 10, textAlign: 'center', fontVariantNumeric: 'tabular-nums slashed-zero' }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <div style={{ display: 'inline-flex', padding: 4, border: `1px solid ${C.line}`, borderRadius: 6 }}>
              <QRCode value={qrValue} size={qrPx} style={{ height: qrPx, width: qrPx }} />
            </div>
            <ImageBox src={data.image} size={qrPx} />
          </div>
          <div style={{ marginTop: 6, fontFamily: MONO, fontSize: 12, fontWeight: 700, color: C.emerald }}>{data.jewelleryNo}</div>
        </div>
      </div>
    );
  }

  // Full tag card.
  const cfg = TAG_LAYOUTS.find((l) => l.id === layoutId) || TAG_LAYOUTS[0];
  const materials = rmMode === 'collapsed' ? collapseMaterials(data.materials) : data.materials;
  const card = (
    <div style={{ width: cfg.fit ? 'fit-content' : cfg.w * s, maxWidth: cfg.w * s, background: C.white, border: `1px solid ${C.ink}`, borderRadius: 8 * s, overflow: 'hidden', fontFamily: SANS, color: C.ink, fontVariantNumeric: 'tabular-nums slashed-zero' }}>
      {renderLayout(layoutId, { data, materials, showNotes: rmMode === 'expanded', qr: cfg.qr * s, qrValue, showQr, showPieces, s, purityPos })}
    </div>
  );
  // Custom exact-size label: wrap the (already scaled) card in a fixed physical
  // box so it prints to exactly boxW×boxH px (= the chosen mm at 96dpi). The box
  // clips anything that overflows, making a non-fitting tag visibly cropped.
  if (boxW && boxH) {
    return (
      <div style={wrap}>
        <div
          style={{
            width: boxW, height: boxH, boxSizing: 'border-box', overflow: 'hidden',
            background: C.white, border: `1px solid ${boxWarn ? '#b45309' : C.line}`,
          }}
        >
          {card}
        </div>
      </div>
    );
  }
  return (
    <div style={wrap}>
      {card}
    </div>
  );
}

// ── Off-screen fit-measurement node ─────────────────────────────────────────
// Renders ONE fit candidate (a specific layout + purity position) at natural
// size (s=1) so a caller can measure its bounding rect and feed the custom-mm
// fit engine (chooseFit). Used by the batch printer to decide, per tag, which
// layout/scale fits an exact label size. Never shown to the user.
export function TagMeasureNode({
  data,
  candidate,
  rmMode = 'expanded',
  showPieces = false,
  showQr = false,
  origin,
  nodeRef,
}) {
  if (!data) return null;
  const mcfg = TAG_LAYOUTS.find((l) => l.id === candidate.layoutId) || TAG_LAYOUTS[0];
  const materials = rmMode === 'collapsed' ? collapseMaterials(data.materials) : data.materials;
  const qrValue = buildQrValue(data, origin);
  return (
    <div
      ref={nodeRef}
      style={{ display: 'inline-block', verticalAlign: 'top', width: mcfg.fit ? 'fit-content' : mcfg.w, maxWidth: mcfg.w, border: `1px solid ${C.ink}`, background: C.white, overflow: 'hidden', fontFamily: SANS, fontVariantNumeric: 'tabular-nums slashed-zero' }}
    >
      {renderLayout(candidate.layoutId, { data, materials, showNotes: rmMode === 'expanded', qr: mcfg.qr, qrValue, showQr, showPieces, s: 1, purityPos: candidate.purityPos })}
    </div>
  );
}
